<template>
	<div
		class="overlay-childproducts"
		:class="{
			'overlay-childproducts--show-summary': showSummary,
		}"
		v-if="visible"
		v-htmlclass="customHtmlClasses"
	>
		<button
			class="overlay-childproducts__close"
			@click.prevent="close"
		>
			<SVGClose/>
		</button>

		<div class="overlay-childproducts__wrapper">
			<div class="overlay-childproducts__content">

				<ul class="overlay-childproducts__steps">
					<li
						class="overlay-childproducts__step"
						:class="{
							'overlay-childproducts__step--active': !extras || currentStep == 'option'
						}"
						v-if="options"
					>
						<button
							class="overlay-childproducts__step-button"
							@click="onSetStep('option')"
							:title="$t('childproducts.optionsTitle')"
						>
							<SVGCategoryCurries class="overlay-childproducts__step-icon"/>
						</button>
					</li>
					<li
						class="overlay-childproducts__step"
						:class="{
							'overlay-childproducts__step--active': !options || currentStep == 'extra'
						}"
						v-if="extras"
					>
						<button
							class="overlay-childproducts__step-button"
							@click="onSetStep('extra')"
							:title="$t('childproducts.extrasTitle')"
						>
							<SVGCategoryExtras class="overlay-childproducts__step-icon"/>
						</button>
					</li>
				</ul>

				<div
					class="overlay-childproducts__list"
					v-for="type in ['option', 'extra']"
					:key="type"
					v-if="childProductTypes[type].length && currentStep == type"
				>
					<h1
						class="overlay-childproducts__list-title"
						v-html="titles[type]"
					/>

					<div
						class="overlay-childproducts__category"
						v-for="category in childProductTypes[type]"
						:key="category.id"
						v-if="category.products.length"
					>
						<h3
							class="overlay-childproducts__category-title"
						>
							<span v-html="category.title"/>
							<small v-html="categoryHint(category)"/>
						</h3>
						<div
							class="overlay-childproducts__category-error"
							v-html="errors[category.taxon]"
							v-if="errors[category.taxon]"
						/>
						<ul
							class="overlay-childproducts__items"
						>
							<li
								class="overlay-childproducts__item"
								v-for="child in category.products"
								:key="child.code"
							>
								<label class="overlay-childproducts__item-label">
									<input
										type="checkbox"
										class="overlay-childproducts__item-input"
										:name="category.taxon"
										:value="child.code"
										:checked="selectedMap[child.code]"
										immediate
										@change="onItemChange(child, category)"
									>
									<figure
										class="overlay-childproducts__item-image"
										v-if="child.image"
									>
										<img
											:src="child.image"
											alt="Product Image"
											class="overlay-childproducts__item-image-element"
										>
									</figure>
									<span
										class="overlay-childproducts__item-name"
										v-html="child.name"
									/>
									<span
										v-if="child.price"
										class="overlay-childproducts__item-price"
										v-html="formatItemPrice(child.price)"
									/>
									<span
										class="overlay-childproducts__item-checkmark"
										:class="{
											'overlay-childproducts__item-checkmark--checked': selectedMap[child.code]
										}"
									>
										<SVGSmallCheckmark class="overlay-childproducts__item-checkmark-icon" />
									</span>
								</label>
							</li>
						</ul>
					</div>
					<div
						class="overlay-childproducts__empty"
						v-if="!options && !extras"
						v-html="$t('childproducts.noOptions')"
					/>
				</div>
			</div>

			<div class="overlay-childproducts__summary">
				<template v-if="currentStep == 'option'">
					<h2
						class="overlay-childproducts__summary-title"
						v-html="$t('childproducts.ingredientsTitle')"
					/>
					<div class="overlay-childproducts__summary-scroll">
						<ul
							class="overlay-childproducts__summary-categories"
						>
							<li
								class="overlay-childproducts__summary-category"
								v-for="category in options"
								:key="`summary-${category.taxon}`"
							>
								<h3
									class="overlay-childproducts__summary-category-title"
									v-html="category.title"
								/>
								<ul
									class="overlay-childproducts__summary-options"
								>
									<li
										class="overlay-childproducts__summary-option"
										v-for="item in category.products"
										:key="`summary-${item.code}`"
										v-if="selectedMap[item.code]"
									>
										<span
											class="overlay-childproducts__summary-option-name"
											v-html="item.name"
										/>
										<span
											class="overlay-childproducts__summary-option-checkmark"
										>
											<SVGSmallCheckmark class="overlay-childproducts__summary-option-checkmark-icon" />
										</span>
									</li>
									<li
										class="overlay-childproducts__summary-option overlay-childproducts__summary-option--empty"
										v-if="!category.products.filter(product => selectedMap[product.code]).length"
									>
										<span
											class="overlay-childproducts__summary-option-name"
											v-html="$t('childproducts.noChoice')"
										/>
									</li>
								</ul>
							</li>
						</ul>
					</div>
				</template>

				<template v-else-if="currentStep == 'extra'">
					<div class="overlay-childproducts__summary-scroll">
						<div class="overlay-childproducts__summary-product">
							<div class="overlay-childproducts__summary-product-title">
								<h3 class="overlay-childproducts__summary-product-name" v-html="item.name" />
								<span class="overlay-childproducts__summary-product-price" v-html="$options.filters.madpricecent(item.price, 'short')" />
							</div>
							<div class="overlay-childproducts__summary-product-options" v-html="variantName" v-if="variantName" />
							<div class="overlay-childproducts__summary-product-options" v-html="selectedOptions.reduce((acc, option) => [...acc, option.name], []).join(', ')" />
						</div>
						<h2
							class="overlay-childproducts__summary-title"
							v-html="$t('childproducts.extrasTitle')"
						/>
						<ul class="overlay-childproducts__summary-extras">
							<li
								class="overlay-childproducts__summary-extra"
								v-for="extra in selectedExtras"
								:key="`summary-extra-${extra.code}`"
							>
								<span class="overlay-childproducts__summary-extra-name" v-html="extra.name" />
								<span class="overlay-childproducts__summary-extra-price" v-html="formatItemPrice(extra.price)" />
							</li>
							<li
								class="overlay-childproducts__summary-extra"
								v-if="!selectedExtras.length"
							>
								<span class="overlay-childproducts__summary-extra-name" v-html="$t('childproducts.noChoiceExtras')" />
							</li>
						</ul>
					</div>
					<div class="overlay-childproducts__summary-total">
						<span class="overlay-childproducts__summary-total-title" v-html="$t('childproducts.total')" />
						<span class="overlay-childproducts__summary-total-price" v-html="$options.filters.madpricecent(total, 'short')" />
					</div>
				</template>
				<Button
					class="overlay-childproducts__summary-button overlay-childproducts__summary-button--desktop"
					@click.prevent="onButtonClick()"
					level="primary"
					type="button"
					:text="buttonText"
				/>
				<Button
					class="overlay-childproducts__summary-button overlay-childproducts__summary-button--mobile"
					@click.prevent="onCloseSummary()"
					level="primary"
					type="button"
					:text="$t('childproducts.backButton')"
				/>
			</div>

			<div class="overlay-childproducts__sticky">
				<div
					class="overlay-childproducts__sticky-details"
					@click.prevent="onOpenSummary()"
				>
					<Button
						class="overlay-childproducts__sticky-details-button"
						type="button"
						level="tertiary"
						:text="$t('childproducts.extrasCount', { number: selectedExtras.length })"
						arrow
					/>
					<div
						class="overlay-childproducts__sticky-details-total"
						v-html="$options.filters.madpricecent(total)"
					/>
				</div>
				<Button
					class="overlay-childproducts__sticky-button"
					@click.prevent="onButtonClick()"
					level="primary"
					type="button"
					:text="buttonText"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex'
import htmlComponent from 'htmlcomponent'
import GenericMixin from '@/mixins/GenericVueMixin';
import OverlayMixin from '@/mixins/OverlayVueMixin';
import Button from '@/components/Button'
import FormInputCheckbox from "@/components/FormInputCheckbox"
import {storeScrollPos, restoreScrollPos} from '@/utils/scrollpos';

export default {
	mixins: [
		GenericMixin,
		OverlayMixin,
	],
	components: {
		Button,
		FormInputCheckbox,
	},
	props: {
		overlayType: {
			type: String,
			default() {
				return 'overlay-childproducts';
			},
		},
		initialVisible: {
			type: Boolean,
			default: false
		},
		htmlClasses: {
			type: Array,
			default: () => ['hide--header', 'show--modal'],
		},
	},
	data() {
		return {
			titles: {
				option: this.$t('childproducts.stepTitleOptions'),
				extra: this.$t('childproducts.stepTitleExtras'),
			},
			errors: {},
			currentStep: 'option',
			showSummary: false,
			item: null,
			selectedCodes: [],
			newItem: false,
			selectedMap: {},
			visible: this.initialVisible
		}
	},
	computed: {
		...mapGetters("childproducts", [
			"childProductsByType",
		]),
		variantName() {
			return this.item.variant != this.item.name && this.item.variant;
		},
		childProductTypes() {
			return Object.keys(this.childProductsByType).reduce((acc, type) => {
				acc[type] = this.childProductsByType[type].map(category => {
					return {
						...category,
						products: category.products
							.filter(product => this.item.availableChildProducts.includes(product.code))
							.map(product => {
								const image = product.images.find(image => image.code == 'shopmanager_product');

								return {
									...product,
									image: image ? image.path : product.images.length && product.images[0].path || "",
								}
							})
					};
				}).filter(category => category.products.length);
				return acc;
			}, {});
		},
		options() {
			return !!this.childProductTypes.option.length && this.childProductTypes.option;
		},
		extras() {
			return !!this.childProductTypes.extra.length && this.childProductTypes.extra;
		},
		selectedOptions() {
			return this.childProductTypes.option.reduce((acc, category) => {
				return [
					...acc,
					...category.products.filter(p => this.selectedMap[p.code]),
				];
			}, []);
		},
		selectedExtras() {
			return this.childProductTypes.extra.reduce((acc, category) => {
				return [
					...acc,
					...category.products.filter(p => this.selectedMap[p.code]),
				];
			}, []);
		},
		selectedItems() {
			return [
				...this.selectedOptions,
				...this.selectedExtras,
			];
		},
		total() {
			const price = this.item.price;
			const itemsPrice = this.selectedItems.reduce((acc, item) => acc + item.price, 0);

			return price + itemsPrice;
		},
		customHtmlClasses() {
			return this.htmlClasses.reduce((acc, cls) => {
				return {...acc, [cls]: this.visible};
			}, {});
		},
		buttonText() {
			if (this.options && this.extras) {
				if (this.currentStep == 'option') {
					return this.$t('childproducts.nextButton');
				}
			}
			return this.newItem ? this.$t('childproducts.buyButton') : this.$t('childproducts.editButton');
		}
	},
	watch: {
		initialVisible(newValue) {
			this.visible = newValue;
		},
	},
	methods: {
		onSetStep(step = 'option') {
			// TODO: handle errors like onButtonClick?
			this.currentStep = step;
		},
		onItemChange(item, category) {
			let selectedMap = {...this.selectedMap};
			const products = category.products.map(product => product.code);
			const count = products.reduce((acc, product) => {
				return selectedMap[product] ? ++acc : acc;
			}, 0);
			const max = category.selectMax;
			const min = category.selectMin;
			let error = '';

			if (selectedMap[item.code]) {
				if (!min || count > 1 || (min && min < max)) {
					selectedMap[item.code] = false;
				}
			}
			else {
				if (max == 1) {
					selectedMap = {
						...selectedMap,
						...products.reduce((acc, product) => {
							return {
								...acc,
								[product]: item.code == product,
							};
						}, {}),
					};
				}
				else if (!max || count < max) {
					selectedMap[item.code] = true;
				}
				else {
					error = this.$t('childproducts.errors.max', { max, over: 1 });
				}
			}

			Vue.set(this.errors, category.taxon, error);
			Vue.set(this, 'selectedMap', selectedMap);
		},
		onButtonClick() {
			let selectedMap = {...this.selectedMap};
			let error = this.childProductTypes[this.currentStep].reduce((acc, category) => {
				const products = category.products.map(product => product.code);
				const count = products.reduce((acc, product) => {
					return selectedMap[product] ? ++acc : acc;
				}, 0);
				const max = category.selectMax;
				const min = category.selectMin;
				let error = '';


				if (count < min) {
					acc = true;
					error = this.$t('childproducts.errors.min', { min });
				}
				else if (count > max) {
					acc = true;
					error = this.$t('childproducts.errors.max', { max, over: count - max });
				}

				Vue.set(this.errors, category.taxon, error);
				return acc;
			}, false);

			if (error) {
				return;
			}

			if (this.options && this.extras) {
				if (this.currentStep == 'option') {
					this.currentStep = 'extra';
					return;
				}
			}
			this.onEdit();
		},
		onEdit() {
			let childCodes = Object.entries(this.selectedMap)
				.reduce((p, [k, v]) => {
					if (v === true) {
						p.push(k)
					}
					return p
				}, [])

			if (this.newItem) {
				this.$emit('add', this.item, childCodes)
			}
			else {
				this.$emit('edit', this.item, childCodes)
			}
		},
		onClose() {
			this.$emit('close')
		},

		onOpenSummary() {
			this.showSummary = true;
		},

		onCloseSummary() {
			this.showSummary = false;
		},

		categoryHint(category) {
			const max = category.selectMax;
			const min = category.selectMin;
			let hint = '';

			if (min && max) {
				if (min == max) {
					hint = this.$t('childproducts.hints.exact', { min, max });
				}
				else {
					hint = this.$t('childproducts.hints.range', { min, max });
				}
			}
			else if (min) {
				hint = this.$t('childproducts.hints.min', { min, max });
			}
			else if (max) {
				hint = this.$t('childproducts.hints.max', { min, max });
			}

			return hint;
		},

		formatItemPrice(price) {
			return `${price >= 0 ? '+' : '-'}&nbsp;${this.$options.filters.madpricecent(Math.abs(price), 'short')}`
		},

		open() {
			this.visible = true;
			this.openOverlay();
			this.$emit('open');
		},
		close() {
			this.visible = false;
			this.closeOverlay();
			this.$emit('close');
		},

		openOverlayHandler(e, data)  {
			if (data.type !== this.overlayType) {
				this.html.classList.remove(this.overlayShowClass);
				this.visible = false;
			}
			else {
				if (!this.overlayIgnoreScrollPos) {
					storeScrollPos();
				}

				this.item = data.item || data.product;
				this.newItem = !!data.product;
				this.selectedCodes = data.selectedCodes || [];
				this.selectedMap = [
					...this.selectedCodes,
				].reduce((acc, code) => {
					return {...acc, [code]: true}
				}, {});

				this.onSetStep(this.options ? 'option' : 'extra');
				this.html.classList.add(this.overlayShowClass);
				this.visible = true;
			}
		},
		closeOverlayHandler(e, data) {
			if (data.type === this.overlayType) {
				this.html.classList.remove(this.overlayShowClass);
				this.visible = false;
				this.showSummary = false;
				this.errors = {};

				if (!this.overlayIgnoreScrollPos) {
					restoreScrollPos();
				}
			}
		},
		toggleOverlayHandler(e, data) {
			if (data.type === this.overlayType) {
				if (this.html.classList.contains(this.overlayShowClass)) {
					htmlComponent.send('overlay:close', {type: this.overlayType});
				}
				else {
					htmlComponent.send('overlay:open', {type: this.overlayType});
				}
			}
		},
	},
	mounted() {
	}
}
</script>

<style>

</style>
